import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import './Payment.css';

const Payment = ({ user, kids = [], showOrders = false }) => {
  const [activeTab, setActiveTab] = useState('services');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateTypes, setRateTypes] = useState([]);
  const [categoryIcons, setCategoryIcons] = useState([]);

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);

  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');

  // Kid-specific rate overrides: { kidId: { rateId: overrideAmount } }
  const [kidRateOverrides, setKidRateOverrides] = useState({});

  // Lookup maps
  const rateLabels = useMemo(() => {
    const m = {}; rateTypes.forEach(rt => { m[rt.key] = rt.label; }); return m;
  }, [rateTypes]);
  const rateIcons = useMemo(() => {
    const m = {}; rateTypes.forEach(rt => { m[rt.key] = rt.icon; }); return m;
  }, [rateTypes]);
  const catIconMap = useMemo(() => {
    const m = {}; categoryIcons.forEach(ci => { m[ci.key] = ci.emoji; }); return m;
  }, [categoryIcons]);

  useEffect(() => { fetchAllData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const kidIds = (kids || []).map(k => k.id).filter(Boolean);
    if (kidIds.length === 0) {
      setKidRateOverrides({});
      return;
    }

    const loadKidOverrides = async () => {
      try {
        const overridesRes = await api.post('/api/kid-rate-overrides/by-kids', { kid_ids: kidIds });
        setKidRateOverrides(overridesRes.data.overrides || {});
      } catch (e) {
        setKidRateOverrides({});
      }
    };

    loadKidOverrides();
  }, [kids]);

  useEffect(() => {
    if (showOrders) {
      setActiveTab('orders');
    }
  }, [showOrders]);

  useEffect(() => {
    if (activeTab === 'history' || activeTab === 'orders') {
      fetchAllData();
    }
  }, [activeTab]);

  const fetchAllData = async () => {
    try {
      setLoading(true); setError('');
      const [servicesRes, walletRes, txnRes, ordersRes] = await Promise.all([
        api.get('/api/services/all'),
        api.get('/api/wallet/balance').catch(() => ({ data: null })),
        api.get('/api/wallet/transactions?limit=20').catch(() => ({ data: { transactions: [] } })),
        api.get('/api/wallet/orders?limit=20').catch(() => ({ data: { orders: [] } }))
      ]);
      const cats = servicesRes.data.categories || [];
      setCategories(cats.filter(c => c.is_active !== false));
      setItems((servicesRes.data.items || []).filter(i => i.is_active !== false));
      setRates((servicesRes.data.rates || []).filter(r => r.is_active !== false));
      const lookups = servicesRes.data.lookups || {};
      setRateTypes(lookups.rate_types || []);
      setCategoryIcons(lookups.category_icons || []);
      setWallet(walletRes.data);
      setTransactions(txnRes.data.transactions || []);
      setOrders(ordersRes.data.orders || []);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally { setLoading(false); }
  };

  const getItemRates = (itemId) => rates.filter(r => r.service_item_id === itemId);
  const getCategoryItems = (catId) => items.filter(i => i.category_id === catId);

  const calcEffective = (rate, kid = null) => {
    // Check for kid-specific override first
    if (kid && kidRateOverrides[String(kid.id)] && kidRateOverrides[String(kid.id)][String(rate.id)] !== undefined) {
      return kidRateOverrides[String(kid.id)][String(rate.id)];
    }
    if (!rate.discount_type || !rate.discount_value) return rate.rate_amount;
    if (rate.discount_type === 'percent') return rate.rate_amount * (1 - rate.discount_value / 100);
    if (rate.discount_type === 'flat') return Math.max(0, rate.rate_amount - rate.discount_value);
    return rate.rate_amount;
  };

  const hasKidOverride = (rate, kid) => {
    return kid && kidRateOverrides[String(kid.id)] && kidRateOverrides[String(kid.id)][String(rate.id)] !== undefined;
  };

  const fmt = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const fmtDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isGroupClassCategory = (cat) => {
    const n = (cat.name || '').toLowerCase();
    return n.includes('group') && n.includes('class');
  };

  // Get the matching group class item for a kid (match on category_name)
  const getKidGroupItem = (catId, kid) => {
    if (!kid?.category_name) return null;
    const catName = kid.category_name.toLowerCase().trim();
    const catItems = getCategoryItems(catId);
    // Try exact match first
    const exact = catItems.find(item => (item.name || '').toLowerCase().trim() === catName);
    if (exact) return exact;
    // Fallback: item name contains category name or vice versa
    return catItems.find(item => {
      const itemName = (item.name || '').toLowerCase().trim();
      return itemName.includes(catName) || catName.includes(itemName);
    }) || null;
  };

  // ─── Cart ───
  const addToCart = useCallback((item, rate, forKid = null) => {
    const effective = calcEffective(rate, forKid);
    const cartKey = `${item.id}-${rate.id}-${forKid?.id || 'general'}`;
    setCart(prev => {
      const existing = prev.find(c => c.cartKey === cartKey);
      if (existing) return prev.map(c => c.cartKey === cartKey ? { ...c, qty: c.qty + 1 } : c);
      // For group classes with a kid, only allow one rate per kid per item — replace any existing
      let updated = prev;
      if (forKid) {
        updated = prev.filter(c => !(c.itemId === item.id && c.forKid?.id === forKid.id));
      }
      return [...updated, {
        cartKey, itemId: item.id, itemName: item.name, rateId: rate.id,
        rateType: rate.rate_type, rateLabel: rateLabels[rate.rate_type] || rate.rate_type,
        unitPrice: effective, originalPrice: rate.rate_amount,
        hasDiscount: !!(rate.discount_type && rate.discount_value > 0), qty: 1,
        forKid: forKid ? { id: forKid.id, name: `${forKid.first_name} ${forKid.last_name || ''}`.trim() } : null
      }];
    });
    setCartOpen(true);
  }, [rateLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFromCart = (cartKey) => setCart(prev => prev.filter(c => c.cartKey !== cartKey));

  const updateCartQty = (cartKey, delta) => {
    setCart(prev => prev.map(c => {
      if (c.cartKey !== cartKey) return c;
      const nq = c.qty + delta;
      return nq > 0 ? { ...c, qty: nq } : c;
    }).filter(c => c.qty > 0));
  };

  const cartTotal = cart.reduce((s, c) => s + c.unitPrice * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const isInCart = (itemId, rateId, kidId) => {
    return cart.find(c => c.cartKey === `${itemId}-${rateId}-${kidId || 'general'}`);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    try {
      setIsCheckingOut(true); setError('');
      const desc = cart.map(c =>
        `${c.itemName} (${c.rateLabel}) x${c.qty}${c.forKid ? ` — ${c.forKid.name}` : ''}`
      ).join('; ');
      const items = cart.map(c => ({
        service_item_id: c.itemId, service_item_name: c.itemName,
        rate_id: c.rateId, rate_type: c.rateType, rate_amount: c.unitPrice,
        quantity: c.qty, kid_id: c.forKid?.id || null, kid_name: c.forKid?.name || null
      }));
      const response = await api.post('/api/wallet/topup/create', { amount: cartTotal, currency: 'USD', description: desc, items });
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      } else {
        setSuccess('Order created successfully!');
        setCart([]); setCartOpen(false);
        await fetchAllData(); setActiveTab('orders');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment.');
    } finally { setIsCheckingOut(false); }
  };

  const handleVerifyPayment = async (orderNumber) => {
    try {
      setVerifyingPayment(true); setPaymentMessage('Verifying...'); setError(''); setSuccess('');
      const res = await api.get(`/api/wallet/topup/verify/${orderNumber}`);
      if (res.data.status === 'COMPLETED') {
        setSuccess('Payment successful! Enrollment updated.'); await fetchAllData(); setActiveTab('orders');
      } else if (res.data.status === 'PROCESSING') { setError('Payment is being processed. Please check back shortly.');
      } else if (res.data.status === 'PENDING') { setError('Payment still pending.');
      } else if (res.data.status === 'EXPIRED') { setError('Payment session expired.');
      } else if (res.data.status === 'FAILED') { setError('Payment failed.'); await fetchAllData();
      } else { setError(`Status: ${res.data.message || res.data.status}`); }
    } catch (err) { setError(err.response?.data?.error || 'Failed to verify');
    } finally { setVerifyingPayment(false); setPaymentMessage(''); }
  };

  // ─── Rate card renderer ───
  const renderRateCard = (item, rate, kid = null) => {
    const effective = calcEffective(rate, kid);
    const hasDiscount = rate.discount_type && rate.discount_value > 0;
    const isOverridden = hasKidOverride(rate, kid);
    const inCart = isInCart(item.id, rate.id, kid?.id);
    // For group classes with a kid: check if ANY rate for this kid+item is in cart
    const kidHasSelection = kid ? cart.find(c => c.itemId === item.id && c.forKid?.id === kid.id) : null;
    const isSelected = inCart;
    const isOtherSelected = kid && kidHasSelection && !isSelected;
    return (
      <div key={rate.id} className={`pay-rate-card${isSelected ? ' pay-rate-card-selected' : ''}${isOverridden ? ' pay-rate-card-override' : ''}`}>
        <div className="pay-rate-card-top">
          <span className="pay-rate-icon">{rateIcons[rate.rate_type] || '💲'}</span>
          <span className="pay-rate-label">{rateLabels[rate.rate_type] || rate.rate_type}</span>
          {isOverridden && <span className="pay-rate-special">Special Rate</span>}
        </div>
        <div className="pay-rate-card-price">
          {(hasDiscount || isOverridden) && <span className="pay-rate-was">{fmt(rate.rate_amount)}</span>}
          <span className="pay-rate-now">{fmt(effective)}</span>
          {hasDiscount && !isOverridden && (
            <span className="pay-rate-save">
              {rate.discount_type === 'percent' ? `${rate.discount_value}% OFF` : `$${rate.discount_value} OFF`}
            </span>
          )}
          {isOverridden && (
            <span className="pay-rate-save">Custom Price</span>
          )}
        </div>
        <div className="pay-rate-card-action">
          {kid ? (
            /* Group class: single selection per kid */
            isSelected ? (
              <button className="pay-add-btn pay-add-btn-selected" onClick={() => removeFromCart(inCart.cartKey)}>✓ Selected</button>
            ) : (
              <button className={`pay-add-btn${isOtherSelected ? ' pay-add-btn-switch' : ''}`} onClick={() => addToCart(item, rate, kid)}>
                {isOtherSelected ? 'Switch' : 'Select'}
              </button>
            )
          ) : (
            /* Non-group: normal add/qty */
            inCart ? (
              <div className="pay-qty-inline">
                <button className="pay-qty-btn" onClick={() => updateCartQty(inCart.cartKey, -1)}>−</button>
                <span className="pay-qty-num">{inCart.qty}</span>
                <button className="pay-qty-btn" onClick={() => updateCartQty(inCart.cartKey, 1)}>+</button>
              </div>
            ) : (
              <button className="pay-add-btn" onClick={() => addToCart(item, rate, kid)}>Add to Cart</button>
            )
          )}
        </div>
      </div>
    );
  };

  // ─── Renders ───
  if (loading) {
    return <div className="pay-container"><div className="pay-loading"><div className="pay-spinner"></div><p>Loading services...</p></div></div>;
  }
  if (verifyingPayment) {
    return <div className="pay-container"><div className="pay-loading"><div className="pay-spinner"></div><h3>Processing Payment</h3><p>{paymentMessage}</p></div></div>;
  }

  const groupCats = categories.filter(c => isGroupClassCategory(c));
  const otherCats = categories.filter(c => !isGroupClassCategory(c));

  return (
    <div className="pay-container">
      {/* ─── Hero ─── */}
      <div className="pay-hero">
        <div className="pay-hero-inner">
          <div>
            <h1 className="pay-hero-title">Payment & Services</h1>
            <p className="pay-hero-sub">Browse and purchase academy services</p>
          </div>
          {wallet && (
            <div className={`pay-balance-card${(wallet.balance || 0) < 0 ? ' pay-balance-negative' : ''}`}>
              <span className="pay-balance-lbl">Wallet Balance</span>
              <span className={`pay-balance-amt${(wallet.balance || 0) < 0 ? ' pay-balance-amt-negative' : ''}`}>{fmt(wallet.balance || 0)}</span>
              {(wallet.balance || 0) < 0 && (
                <button
                  className="pay-balance-pay-btn"
                  onClick={async () => {
                    try {
                      setError('');
                      const amount = Math.abs(wallet.balance);
                      const response = await api.post('/api/wallet/topup/create', {
                        amount,
                        currency: 'USD',
                        description: 'Pay outstanding balance'
                      });
                      if (response.data.payment_url) {
                        window.location.href = response.data.payment_url;
                      }
                    } catch (err) {
                      setError(err.response?.data?.error || 'Failed to initiate payment');
                    }
                  }}
                >
                  💳 Pay {fmt(Math.abs(wallet.balance))}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && <div className="pay-alert pay-alert-err"><span>⚠️</span><span>{error}</span><button onClick={() => setError('')}>×</button></div>}
      {success && <div className="pay-alert pay-alert-ok"><span>✅</span><span>{success}</span><button onClick={() => setSuccess('')}>×</button></div>}

      {/* ─── Tabs + Cart ─── */}
      <div className="pay-nav">
        <div className="pay-tabs">
          {[
            { key: 'services', icon: '🛍️', label: 'Services' },
            { key: 'history', icon: '📋', label: 'History' },
            { key: 'orders', icon: '📦', label: 'Orders' }
          ].map(t => (
            <button key={t.key} className={`pay-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
              <span>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </div>
        <button className={`pay-cart-btn ${cartCount > 0 ? 'has-items' : ''}`} onClick={() => setCartOpen(!cartOpen)}>
          🛒{cartCount > 0 && <span className="pay-cart-count">{cartCount}</span>}
          {cartCount > 0 && <span className="pay-cart-amt">{fmt(cartTotal)}</span>}
        </button>
      </div>

      {/* ─── Cart Panel ─── */}
      {cartOpen && (
        <div className="pay-cart-panel">
          <div className="pay-cart-head">
            <h3>🛒 Your Cart ({cartCount})</h3>
            <button className="pay-close" onClick={() => setCartOpen(false)}>×</button>
          </div>
          {cart.length === 0 ? (
            <div className="pay-cart-empty"><p>Your cart is empty. Add services to get started.</p></div>
          ) : (
            <>
              <div className="pay-cart-list">
                {cart.map(c => (
                  <div key={c.cartKey} className="pay-cart-row">
                    <div className="pay-cart-row-info">
                      <strong>{c.itemName}</strong>
                      <span className="pay-cart-row-meta">{c.rateLabel}{c.forKid && <> &middot; <em>{c.forKid.name}</em></>}</span>
                    </div>
                    <div className="pay-cart-row-right">
                      <div className="pay-qty-inline sm">
                        <button className="pay-qty-btn" onClick={() => updateCartQty(c.cartKey, -1)}>−</button>
                        <span className="pay-qty-num">{c.qty}</span>
                        <button className="pay-qty-btn" onClick={() => updateCartQty(c.cartKey, 1)}>+</button>
                      </div>
                      <span className="pay-cart-row-price">{fmt(c.unitPrice * c.qty)}</span>
                      <button className="pay-remove" onClick={() => removeFromCart(c.cartKey)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pay-cart-foot">
                <div className="pay-cart-total-row">
                  <span>Total</span><span className="pay-cart-total-amt">{fmt(cartTotal)}</span>
                </div>
                <button className="pay-checkout-btn" onClick={handleCheckout} disabled={isCheckingOut}>
                  {isCheckingOut ? <><span className="pay-spinner-sm"></span> Processing...</> : <>💳 Proceed to Pay {fmt(cartTotal)}</>}
                </button>
                <p className="pay-secure-note">🔒 Secure payment processing</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════ SERVICES TAB ═══════ */}
      {activeTab === 'services' && (
        <div className="pay-services">
          {categories.length === 0 ? (
            <div className="pay-empty-state"><div className="pay-empty-icon">🏪</div><h3>No Services Available</h3><p>Services will appear here once configured.</p></div>
          ) : (
            <>
              {/* ─── Group Classes: show per-kid ─── */}
              {groupCats.map(cat => {
                const catIcon = catIconMap[cat.icon] || '🎓';
                const allCatItems = getCategoryItems(cat.id);

                return (
                  <div key={cat.id} className="pay-section">
                    <div className="pay-section-head">
                      <div className="pay-section-icon">
                        {cat.image_url ? <img src={cat.image_url} alt="" /> : <span>{catIcon}</span>}
                      </div>
                      <div>
                        <h2>{cat.name}</h2>
                        {cat.description && <p>{cat.description}</p>}
                      </div>
                    </div>

                    {kids.length > 0 ? (
                      <div className="pay-kid-groups">
                        {kids.map(kid => {
                          const matchItem = getKidGroupItem(cat.id, kid);
                          if (!matchItem) return (
                            <div key={kid.id} className="pay-kid-card">
                              <div className="pay-kid-card-head">
                                <div className="pay-kid-avatar">
                                  {kid.profile_pic_url ? <img src={kid.profile_pic_url} alt="" /> : <span>{(kid.first_name || '?')[0]}</span>}
                                </div>
                                <div className="pay-kid-info">
                                  <strong>{kid.first_name} {kid.last_name || ''}</strong>
                                  <span className="pay-kid-cat">{kid.category_name || 'Not assigned'}</span>
                                </div>
                              </div>
                              <div className="pay-kid-no-class">No matching group class for <strong>{kid.category_name || 'unassigned'}</strong></div>
                            </div>
                          );

                          const itemRates = getItemRates(matchItem.id);
                          return (
                            <div key={kid.id} className="pay-kid-card">
                              <div className="pay-kid-card-head">
                                <div className="pay-kid-avatar">
                                  {kid.profile_pic_url ? <img src={kid.profile_pic_url} alt="" /> : <span>{(kid.first_name || '?')[0]}</span>}
                                </div>
                                <div className="pay-kid-info">
                                  <strong>{kid.first_name} {kid.last_name || ''}</strong>
                                  <span className="pay-kid-cat">{kid.category_name}</span>
                                </div>
                                <div className="pay-kid-class-badge">{matchItem.name}</div>
                              </div>
                              {matchItem.image_url && (
                                <div className="pay-kid-class-img"><img src={matchItem.image_url} alt={matchItem.name} /></div>
                              )}
                              {matchItem.description && <p className="pay-kid-class-desc">{matchItem.description}</p>}
                              {itemRates.length > 0 ? (
                                <div className="pay-rate-cards">
                                  {itemRates.map(rate => renderRateCard(matchItem, rate, kid))}
                                </div>
                              ) : (
                                <div className="pay-no-rates">Pricing not configured yet</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* No kids: show all group class items */
                      <div className="pay-items-list">
                        {allCatItems.map(item => {
                          const itemRates = getItemRates(item.id);
                          return (
                            <div key={item.id} className="pay-service-card">
                              {item.image_url && <div className="pay-service-img"><img src={item.image_url} alt={item.name} /></div>}
                              <div className="pay-service-body">
                                <h3>{item.name}</h3>
                                {item.description && <p className="pay-service-desc">{item.description}</p>}
                                {itemRates.length > 0 ? (
                                  <div className="pay-rate-cards">{itemRates.map(rate => renderRateCard(item, rate))}</div>
                                ) : (<div className="pay-no-rates">Pricing not configured yet</div>)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ─── Other categories (Lanes, etc.) ─── */}
              {otherCats.map(cat => {
                const catIcon = catIconMap[cat.icon] || '📦';
                const catItems = getCategoryItems(cat.id);
                return (
                  <div key={cat.id} className="pay-section">
                    <div className="pay-section-head">
                      <div className="pay-section-icon">
                        {cat.image_url ? <img src={cat.image_url} alt="" /> : <span>{catIcon}</span>}
                      </div>
                      <div>
                        <h2>{cat.name}</h2>
                        {cat.description && <p>{cat.description}</p>}
                      </div>
                    </div>
                    {catItems.length === 0 ? (
                      <div className="pay-no-items">No items available.</div>
                    ) : (
                      <div className="pay-items-grid">
                        {catItems.map(item => {
                          const itemRates = getItemRates(item.id);
                          return (
                            <div key={item.id} className="pay-service-card">
                              {item.image_url && <div className="pay-service-img"><img src={item.image_url} alt={item.name} /></div>}
                              <div className="pay-service-body">
                                <div className="pay-service-head">
                                  <h3>{item.name}</h3>
                                  {item.max_players > 0 && <span className="pay-meta-chip">👥 Max {item.max_players}</span>}
                                </div>
                                {item.description && <p className="pay-service-desc">{item.description}</p>}
                                {item.length_ft > 0 && <div className="pay-spec">📏 {item.length_ft} ft</div>}
                                {itemRates.length > 0 ? (
                                  <div className="pay-rate-cards">{itemRates.map(rate => renderRateCard(item, rate))}</div>
                                ) : (<div className="pay-no-rates">Pricing not configured yet</div>)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ═══ HISTORY ═══ */}
      {activeTab === 'history' && (
        <div className="pay-tab-content">
          <div className="pay-tab-head"><h2>Transaction History</h2><button className="pay-refresh" onClick={fetchAllData}>🔄 Refresh</button></div>
          {transactions.length > 0 ? (
            <div className="pay-txn-list">
              {transactions.map(txn => (
                <div key={txn.id} className="pay-txn">
                  <div className="pay-txn-icon">{txn.transaction_type === 'CREDIT' ? '💰' : txn.transaction_type === 'DEBIT' ? '💸' : '💵'}</div>
                  <div className="pay-txn-info">
                    <div className="pay-txn-desc">{txn.description || txn.transaction_type}</div>
                    <div className="pay-txn-date">{fmtDate(txn.created_at)}</div>
                  </div>
                  <div className={`pay-txn-amt ${txn.transaction_type === 'CREDIT' ? 'credit' : 'debit'}`}>
                    {txn.transaction_type === 'CREDIT' ? '+' : '-'}{fmt(txn.amount)}
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="pay-empty-state"><div className="pay-empty-icon">📋</div><h3>No Transactions Yet</h3></div>}
        </div>
      )}

      {/* ═══ ORDERS ═══ */}
      {activeTab === 'orders' && (
        <div className="pay-tab-content">
          <div className="pay-tab-head"><h2>Payment Orders</h2><button className="pay-refresh" onClick={fetchAllData}>🔄 Refresh</button></div>
          {orders.length > 0 ? (
            <div className="pay-orders-grid">
              {orders.map(order => (
                <div key={order.id} className="pay-order">
                  <div className="pay-order-top">
                    <span className="pay-order-num">#{order.order_number}</span>
                    <span className={`pay-badge pay-badge-${(order.status || '').toLowerCase()}`}>{order.status}</span>
                  </div>
                  <div className="pay-order-body">
                    <div className="pay-order-row"><span>Amount</span><strong>{fmt(order.amount)}</strong></div>
                    <div className="pay-order-row"><span>Type</span><span>{order.order_type}</span></div>
                    <div className="pay-order-row"><span>Created</span><span>{fmtDate(order.created_at)}</span></div>
                    {order.completed_at && <div className="pay-order-row"><span>Completed</span><span>{fmtDate(order.completed_at)}</span></div>}
                  </div>
                  {(order.status === 'PROCESSING' || order.status === 'PENDING') && (
                    <div className="pay-order-actions">
                      <button className="pay-btn-outline" onClick={() => handleVerifyPayment(order.order_number)}>Check Status</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : <div className="pay-empty-state"><div className="pay-empty-icon">📦</div><h3>No Orders Yet</h3></div>}
        </div>
      )}

      {/* Mobile FAB */}
      {cartCount > 0 && !cartOpen && (
        <button className="pay-fab" onClick={() => setCartOpen(true)}>
          🛒<span className="pay-fab-count">{cartCount}</span><span className="pay-fab-total">{fmt(cartTotal)}</span>
        </button>
      )}
    </div>
  );
};

export default Payment;